import React from 'react';
import { Modal as AntModal } from 'antd';
import styled from 'styled-components';
import { Button } from '../Button/Button';
import { IconClose } from '../Icons';
import { windowMaxWidth } from '../Layout/breakpoints';

const ONBOARDING_IMPORT_MODAL_VERTICAL_GAP = 60;
const ONBOARDING_IMPORT_MODAL_HEADER_HEIGHT = 240;
const ONBOARDING_IMPORT_MODAL_FOOTER_HEIGHT = 91;
const ONBOARDING_IMPORT_MODAL_BODY_OFFSET = `${
  ONBOARDING_IMPORT_MODAL_VERTICAL_GAP + ONBOARDING_IMPORT_MODAL_HEADER_HEIGHT + ONBOARDING_IMPORT_MODAL_FOOTER_HEIGHT
}px`;

const ExtendedModal = styled(AntModal)`
  .ant-modal-container {
    background: var(--greyscale-white);
    border-radius: var(--border-radius-regular);
    padding: 0;

    .ant-modal-close {
      align-items: center;
      border-radius: var(--border-radius-small);
      display: flex;
      height: 30px;
      justify-content: center;
      right: 20px;
      top: 20px;
      width: 30px;

      .ant-modal-close-x {
        display: flex;
        height: 20px;
        width: 20px;
      }

      &:hover {
        background-color: var(--secondary-whisper);
      }
    }

    .ant-modal-header {
      align-items: center;
      background: var(--greyscale-white);
      border-bottom: 1px solid var(--greyscale-dark-white);
      border-radius: var(--border-radius-regular) 8px 0 0;
      display: flex;
      height: 70px;
      justify-content: space-around;
      margin-bottom: 0;

      .ant-modal-title {
        color: var(--greyscale-blackish);
        font-size: var(--font-size-heading-2);
        font-weight: var(--font-weight-medium);
        letter-spacing: var(--font-letter-spacing-small);
        line-height: var(--font-line-height-large);
      }
    }
  }

  &.ant-modal {
    top: 58px;

    .ant-modal-body {
      font-size: var(--font-size-regular);
      overflow: auto;
      padding: 20px 40px 30px;
      position: relative;
    }

    .ant-modal-footer {
      background: transparent;
      border-radius: 0 0 8px 8px;
      border-top: 1px solid var(--greyscale-dark-white);
      margin-top: 0;
      padding: 20px 30px 30px;
      text-align: right;
    }

    .ant-modal-close-icon {
      content: '\\e90e';
      font-size: var(--font-size-regular);
    }

    @media (${windowMaxWidth.mobile}) {
      top: 0;
      max-width: 100%;
      margin: 0;
      padding: 0;

      .ant-modal-header {
        border-radius: 0;
        height: 60px;

        .ant-modal-title {
          font-size: var(--font-size-heading-3);
          display: inline-block;
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .ant-modal-container {
        border-radius: 0;
        overflow-x: hidden;

        .ant-modal-close {
          top: 15px;
        }
      }

      .ant-modal-body {
        min-height: calc(100vh - 141px);
        padding: 20px;
        height: auto;

        @supports (height: 100dvh) {
          min-height: calc(100dvh - 141px);
        }
      }

      .ant-modal-footer {
        padding: 20px;
      }
    }
  }

  &.modal {
    background: var(--greyscale-white);

    &-no {
      &-padding {
        .ant-modal-body {
          padding: 0;
        }
      }
    }

    &-settlements {
      .ant-modal-body {
        height: 500px;
        overflow: auto;
        padding: 0;
        scrollbar-color: rgb(136 136 136 / 30%) transparent;
        scrollbar-width: thin;
      }
    }

    &-custom-header {
      .ant-modal-header {
        height: 80px;
        padding-bottom: 10px;
        padding-top: 20px;
      }
    }
  }

  &.no-padding {
    .ant-modal-body {
      padding: 0;
    }
  }

  &.claim-participants {
    .ant-modal-body {
      height: 500px;
      overflow: auto;
      padding: 0;
      scrollbar-color: rgb(136 136 136 / 30%) transparent;
      scrollbar-width: thin;

      > div {
        display: flex;
        flex-direction: column;
      }
    }
  }

  &.centered-modal {
    .ant-modal-body {
      height: auto;
      min-height: auto;
    }
  }

  &.worklist-message-reminder-modal {
    animation-duration: 0.2s !important;
    height: calc(100vh - 60px);
    padding: 0;
    position: absolute;
    right: 0;
    top: 0;

    .ant-modal-container {
      background-color: var(--secondary-whisper);
      border-radius: unset;
      box-shadow: none;
      display: flex;
      flex-direction: column;
      height: 100%;
      justify-content: space-between;

      .ant-modal-close-x {
        height: 40px;
        padding-right: 20px;
        padding-top: 20px;
        width: 40px;

        .close-icon-wrapper {
          height: 20px;
          width: 20px;

          svg {
            path {
              stroke: var(--primary-oqio-blue);
            }
          }

          &:hover {
            background-color: initial;
          }
        }

        &:hover {
          .close-icon-wrapper {
            svg {
              path {
                stroke: var(--primary-oqio-blue-hover);
              }
            }

            &::before {
              color: var(--primary-oqio-blue-hover);
            }
          }
        }
      }

      .ant-modal-header {
        align-items: flex-end;
        border-radius: unset;
        flex: 1 0 101px;
        height: 100px;
        justify-content: flex-start;

        .ant-modal-title {
          color: var(--greyscale-blackish);
          line-height: var(--font-line-height-large);
        }
      }

      .ant-modal-body {
        flex: 1 1 100%;
        overflow: auto;
        padding: 20px;
      }
    }

    .ant-modal-footer {
      background-color: var(--greyscale-white);
      height: 80px;
      padding: 20px;
    }
  }

  &.initial-signin-modal {
    top: 10%;

    @media (${windowMaxWidth.mobile}) {
      top: 0;

      .ant-modal-body {
        min-height: calc(100dvh - 70px);
        padding: 20px;
      }
    }
  }

  &.import-custom-headers-modal {
    .ant-modal-container {
      .ant-modal-body {
        height: 500px;
        overflow: auto;
        scrollbar-color: rgb(136 136 136 / 30%) transparent;
        scrollbar-width: thin;
      }
    }
  }

  &.onboarding-import-file-upload-modal,
  &.import-file-check-modal {
    .ant-modal-container {
      .ant-modal-body {
        height: 320px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  &.policy-premiums-modal {
    .ant-modal-container {
      .ant-modal-body {
        height: 50vh;
        overflow: auto;
        scrollbar-color: rgb(136 136 136 / 30%) transparent;
        scrollbar-width: thin;
      }
    }
  }

  &.onboarding-import-modal,
  &.import-preview-modal {
    height: calc(100vh - ${ONBOARDING_IMPORT_MODAL_VERTICAL_GAP}px);
    margin: 0 auto;
    max-height: calc(100vh - ${ONBOARDING_IMPORT_MODAL_VERTICAL_GAP}px);
    padding-bottom: 0;
    top: 30px;

    @supports (height: 100dvh) {
      height: calc(100dvh - ${ONBOARDING_IMPORT_MODAL_VERTICAL_GAP}px);
      max-height: calc(100dvh - ${ONBOARDING_IMPORT_MODAL_VERTICAL_GAP}px);
    }

    .ant-modal-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      max-height: calc(100vh - ${ONBOARDING_IMPORT_MODAL_VERTICAL_GAP}px);
      overflow: hidden;
      padding: 0;

      @supports (height: 100dvh) {
        max-height: calc(100dvh - ${ONBOARDING_IMPORT_MODAL_VERTICAL_GAP}px);
      }

      .ant-modal-header {
        align-items: flex-start;
        flex: 0 0 auto;
        height: ${ONBOARDING_IMPORT_MODAL_HEADER_HEIGHT}px;
        justify-content: flex-start;
        padding: 20px;
        overflow: hidden;

        .ant-modal-title {
          width: 100%;
          display: flex;
          justify-content: center;
          padding: 20px;
        }
      }

      .ant-modal-body {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        height: calc(100vh - ${ONBOARDING_IMPORT_MODAL_BODY_OFFSET});
        min-height: calc(100vh - ${ONBOARDING_IMPORT_MODAL_BODY_OFFSET});
        overflow: hidden;
        padding: 0;

        @supports (height: 100dvh) {
          height: calc(100dvh - ${ONBOARDING_IMPORT_MODAL_BODY_OFFSET});
          min-height: calc(100dvh - ${ONBOARDING_IMPORT_MODAL_BODY_OFFSET});
        }
      }

      .ant-modal-footer {
        align-items: center;
        display: flex;
        flex: 0 0 auto;
        height: ${ONBOARDING_IMPORT_MODAL_FOOTER_HEIGHT}px;
        justify-content: flex-end;
        padding: 20px 30px 30px;
      }
    }
  }

  &.modal-title-ellipsis {
    /* stylelint-disable-next-line no-descending-specificity */
    .ant-modal-title {
      max-width: 70%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

const NoPointerEventsContainer = styled.div`
  .ant-modal-wrap {
    pointer-events: none;
  }
`;

ExtendedModal.confirm = AntModal.confirm;

const closeIcon = (
  <div className="close-icon-wrapper">
    <IconClose />
  </div>
);

export const StyledFooter = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ModalFooter = ({ ...props }) => {
  const { onCancel, onSubmit, disableSubmit, isButtonLoading } = props;
  const disableSubmitButton = disableSubmit || false;
  return (
    <StyledFooter>
      <Button onClick={onCancel} type="secondary">
        Abbrechen
      </Button>
      <Button disabled={disableSubmitButton} type="primary" onClick={onSubmit} loading={isButtonLoading}>
        Speichern
      </Button>
    </StyledFooter>
  );
};

const getDefaultContainer = () => {
  const container = document.getElementById('page-modal-wrapper');
  return container;
};

const Modal = ({ children, ...props }) => {
  const footer = props.footer || <ModalFooter {...props} />;
  const getContainer = props.getContainer || getDefaultContainer();
  return props.createNoPointerEventsContainer ? (
    <NoPointerEventsContainer footer={footer} id="no-pointer-events-container">
      <ExtendedModal closeIcon={closeIcon} {...props} getContainer="#no-pointer-events-container">
        {children}
      </ExtendedModal>
    </NoPointerEventsContainer>
  ) : (
    <ExtendedModal width={600} footer={footer} closeIcon={closeIcon} {...props} getContainer={getContainer}>
      {children}
    </ExtendedModal>
  );
};

export default Modal;
